import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { resolveLocale } from '../lib/dateFormat';
import { useAuthStore, useUIStore } from '../store/index';
import { Task, User, WorkspaceGroup, ImpactDomain, Project } from '../types';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import TaskDetailPanel from '../components/TaskDetailPanel';
import { useConfirm } from '../components/ConfirmDialogProvider';

// 10MB in bytes — soft client-side limit for cumulative file selection.
// The backend enforces the real per-task total size limit; this only
// provides early UX feedback so oversized files aren't silently dropped.
const MAX_TOTAL_SIZE_BYTES = 10 * 1024 * 1024;

interface SelectedImage {
  file: File;
  preview: string;
}

interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  domain_id: string | null;
  project_id: string | null;
  assigned_to_user_id: string | null;
  assigned_to_group_id: string | null;
}

  const Tasks: React.FC = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const user = useAuthStore((s) => s.user);
  // client_user cannot create tasks (client accounts report incidents, not
  // drive internal work items) — matches backend's POST /tasks role check.
  const canCreateTask = user && (user.role === 'superadmin' || user.role === 'manager' || user.role === 'operator');

  // Data states
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [domains, setDomains] = useState<ImpactDomain[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create task modal
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [taskCreating, setTaskCreating] = useState(false);
  const [taskCreateError, setTaskCreateError] = useState<string | null>(null);

  // Selected images for the create-task modal
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);

  // Task form state
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [domainId, setDomainId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskPriority, setTaskPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [assignType, setAssignType] = useState<'user' | 'group'>('user');
  const [assignId, setAssignId] = useState('');
  const [watcherUserIds, setWatcherUserIds] = useState<string[]>([]);
  const [watcherGroupIds, setWatcherGroupIds] = useState<string[]>([]);

  // Sorting — client-side, since the full (already-visibility-filtered)
  // task list is fetched once and all filters below are applied in the
  // browser too (see FaZ 3 backend notes: TaskService.listTasks can't
  // paginate in SQL without duplicating its assignee/watcher visibility
  // filter there, so this page keeps its existing fetch-everything-once
  // shape and only adds real page/size slicing on top of it).
  type TaskSortColumn = 'task_number' | 'title' | 'status' | 'priority' | 'created_at';
  const [sortBy, setSortBy] = useState<TaskSortColumn>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination — client-side, slicing the filtered+sorted array below.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [searchParams] = useSearchParams();
  const [filterDomainIds, setFilterDomainIds] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState(() => {
    const v = searchParams.get('status');
    return v === 'todo' || v === 'claimed' || v === 'completed' ? v : '';
  });
  const [filterAssigneeId, setFilterAssigneeId] = useState(() => (searchParams.get('assignee') === 'me' && user ? user.id : ''));
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  // Silent, URL-only filters (no visible dropdown) — driven by Home widget
  // "view all" links, e.g. /tasks?status=open or ?group=mine.
  const [filterOpenOnly] = useState<boolean>(() => searchParams.get('status') === 'open');
  const [filterGroupMine] = useState<boolean>(() => searchParams.get('group') === 'mine');
  const [filterCreatedByMe] = useState<boolean>(() => searchParams.get('createdByMe') === 'true');
  const [filterUnassigned] = useState<boolean>(() => searchParams.get('unassigned') === 'true');

  // Bulk selection — scoped to the tasks visible on the current page only
  // (no cross-page "select all" concept, keeping this simple).
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [bulkAssigneeId, setBulkAssigneeId] = useState('');
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [bulkActionError, setBulkActionError] = useState<string | null>(null);

  // Density (compact/comfortable) — global UI preference, persisted in the
  // same zustand ui-storage as theme/language and shared across pages.
  // Toggled from the Sidebar; this page only reads it for row density.
  const { density, language } = useUIStore();

  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // Which row action icon opened the detail panel — only Reassign needs
  // to pre-expand its own section, Claim/Complete act contextually.
  const [panelFocusReassign, setPanelFocusReassign] = useState(false);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateDeletingId, setTemplateDeletingId] = useState<string | null>(null);

  // ---- Data Fetching ----

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [membersRes, groupsRes, domainsRes, projectsRes, tasksRes] = await Promise.all([
        api.get('/workspace/members'),
        api.get('/workspace/groups'),
        api.get('/workspace/domains'),
        api.get('/projects'),
        api.get('/tasks')
      ]);

      const mData = (membersRes.data as any)?.data;
      if (Array.isArray(mData)) setMembers(mData);
      const gData = (groupsRes.data as any)?.data;
      if (Array.isArray(gData)) setGroups(gData);
      const dData = (domainsRes.data as any)?.data;
      if (Array.isArray(dData)) setDomains(dData);
      const pData = (projectsRes.data as any)?.data;
      if (Array.isArray(pData)) setProjects(pData);

      const tData = (tasksRes.data as any)?.data;
      const tasksList: Task[] = Array.isArray(tData) ? tData : [];
      setTasks(tasksList);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load tasks.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Any filter/sort change starts back at page 1 — a stale page number
  // from a wider result set could otherwise land past the new last page.
  useEffect(() => {
    setPage(1);
  }, [filterTitle, filterProjectId, filterDomainIds, filterStatus, filterAssigneeId, sortBy, sortOrder]);

  // ---- Task Handlers ----

  const resetForm = () => {
    setTaskTitle('');
    setTaskDesc('');
    setDomainId('');
    setProjectId(projects.find((p) => p.is_default === 1)?.id || '');
    setAssignType('user');
    setAssignId('');
    setWatcherUserIds([]);
    setWatcherGroupIds([]);
    setTaskCreateError(null);
    setUploadWarnings([]);
    // Revoke object URLs to avoid memory leaks
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  const openTemplatePicker = async () => {
    setShowTemplatePicker(true);
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const res = await api.get('/tasks/templates');
      const data = (res.data as any)?.data;
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setTemplatesError(getApiErrorMessage(err, 'Failed to load templates.'));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleUseTemplate = (template: TaskTemplate) => {
    resetForm();
    setTaskTitle(template.title);
    setTaskDesc(template.description);
    if (template.domain_id) setDomainId(template.domain_id);
    if (template.project_id) setProjectId(template.project_id);
    if (template.assigned_to_user_id) {
      setAssignType('user');
      setAssignId(template.assigned_to_user_id);
    } else if (template.assigned_to_group_id) {
      setAssignType('group');
      setAssignId(template.assigned_to_group_id);
    }
    setShowTemplatePicker(false);
    setShowCreateTask(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    setTemplateDeletingId(templateId);
    try {
      await api.delete(`/tasks/templates/${templateId}`);
      setTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
    } catch (err: any) {
      setTemplatesError(getApiErrorMessage(err, 'Failed to delete template.'));
    } finally {
      setTemplateDeletingId(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const warnings: string[] = [];
    const newImages: SelectedImage[] = [];

    // Running total across already-selected files + files accepted so far
    // in this batch — each incoming file is checked against the actual
    // cumulative total, not just its own size, so three individually-small
    // files that together exceed 10MB are correctly rejected.
    let runningTotal = selectedImages.reduce((sum, img) => sum + img.file.size, 0);

    files.forEach((file) => {
      if (runningTotal + file.size > MAX_TOTAL_SIZE_BYTES) {
        warnings.push(
          `${file.name} (${formatFileSize(file.size)}) ${t('tasks.board.fileTooLarge')}`
        );
        return;
      }
      runningTotal += file.size;
      newImages.push({
        file,
        preview: URL.createObjectURL(file),
      });
    });

    if (warnings.length > 0) {
      setUploadWarnings(warnings);
    }

    setSelectedImages((prev) => [...prev, ...newImages]);
    // Reset input value so the same file can be selected again if removed
    e.target.value = '';
  };

  const removeSelectedImage = (index: number) => {
    setSelectedImages((prev) => {
      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];
      URL.revokeObjectURL(removed.preview);
      return updated;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setTaskCreating(true);
    setTaskCreateError(null);
    setUploadWarnings([]);

    try {
      // 1) Create the task via JSON (unchanged from existing behavior)
      const payload: Record<string, any> = {
        title: taskTitle,
        description: taskDesc,
        domain_id: domainId,
        project_id: projectId,
        priority: taskPriority
      };
      if (assignId) {
        if (assignType === 'user') {
          payload.assigned_to_user_id = assignId;
        } else {
          payload.assigned_to_group_id = assignId;
        }
      }
      if (watcherUserIds.length > 0) {
        payload.watcher_user_ids = watcherUserIds;
      }
      if (watcherGroupIds.length > 0) {
        payload.watcher_group_ids = watcherGroupIds;
      }

      const res = await api.post('/tasks', payload);
      const created = (res.data as any)?.data as Task;

      // 2) Upload each selected image as a separate FormData request.
      //    Backend expects a single "image" field per request.
      const taskId = created?.id;

      if (taskId && selectedImages.length > 0) {
        const warnings: string[] = [];

        for (const img of selectedImages) {
          try {
            const formData = new FormData();
            formData.append('image', img.file);

            await api.post(`/tasks/${taskId}/attachments`, formData, {
              headers: {},
            });
          } catch (uploadErr: any) {
            if (axios.isAxiosError(uploadErr) && uploadErr.response?.status === 409 && uploadErr.response.data?.error?.code === 'QUOTA_EXCEEDED') {
              const { candidates, neededBytes } = uploadErr.response.data.data;
              const names = candidates.map((c: any) => c.original_name).join(', ');
              const confirmMsg = t('fileQuota.confirmPrompt', { count: candidates.length, names, size: formatFileSize(neededBytes) });
              if (await confirm(confirmMsg)) {
                try {
                  const retryFormData = new FormData();
                  retryFormData.append('image', img.file);
                  retryFormData.append('confirmEviction', 'true');
                  await api.post(`/tasks/${taskId}/attachments`, retryFormData, {
                    headers: {},
                  });
                  continue;
                } catch (retryErr: any) {
                  const msg = getApiErrorMessage(retryErr, t('tasks.board.uploadFailed'));
                  warnings.push(`${img.file.name}: ${msg}`);
                  continue;
                }
              }
            }
            const msg = getApiErrorMessage(uploadErr, t('tasks.board.uploadFailed'));
            warnings.push(`${img.file.name}: ${msg}`);
          }
        }

        if (warnings.length > 0) {
          // Use alert since the modal is about to close and state would be reset
          alert(warnings.join('\n'));
        }
      }

      // 3) Refresh list + close modal (mirrors original flow)
      if (created) {
        setTasks((prev) => [created, ...prev]);
      } else {
        await fetchAllData();
      }
      setShowCreateTask(false);
      resetForm();
    } catch (err: any) {
      setTaskCreateError(getApiErrorMessage(err, 'Failed to create task.'));
    } finally {
      setTaskCreating(false);
    }
  };

  // ---- Helpers ----

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; labelKey: string }> = {
      todo: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', labelKey: 'tasks.detail.status.todo' },
      claimed: { color: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30', labelKey: 'tasks.detail.status.claimed' },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', labelKey: 'tasks.detail.status.completed' }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', labelKey: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {t(c.labelKey)}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const config: Record<string, { color: string; labelKey: string }> = {
      low: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', labelKey: 'tasks.detail.priority.low' },
      medium: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', labelKey: 'tasks.detail.priority.medium' },
      high: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', labelKey: 'tasks.detail.priority.high' },
      urgent: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', labelKey: 'tasks.detail.priority.urgent' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {t(c.labelKey)}
      </span>
    );
  };

  // Solid status color, used for the thin bar next to the task title —
  // deliberately still status (not priority, added later) to keep the
  // bar's meaning consistent with getStatusBadge's color family; priority
  // has its own badge (getPriorityBadge) and filterable column instead.
  const getStatusBarColor = (status: string): string => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-400 dark:bg-gray-500',
      claimed: 'bg-amber-500',
      completed: 'bg-green-500'
    };
    return colors[status] || 'bg-gray-400 dark:bg-gray-500';
  };

  const getAssigneeName = (task: Task): string => {
    if (task.assigned_to_user_id) {
      const m = members.find((m) => m.id === task.assigned_to_user_id);
      return m ? m.name : task.assigned_to_user_id;
    }
    if (task.assigned_to_group_id) {
      const g = groups.find((g) => g.id === task.assigned_to_group_id);
      return g ? g.name : task.assigned_to_group_id;
    }
    return '-';
  };

  const getDomainName = (domainId: string | null): string => {
    if (!domainId) return '-';
    const d = domains.find((d) => d.id === domainId);
    return d ? d.name : domainId;
  };

  // Every group the user is in (a user can be in several groups); group_id is only the primary one.
  const myGroupIds: string[] = user?.group_ids ?? (user?.group_id ? [user.group_id] : []);

  const canActOnTask = (task: Task): boolean => {
    if (!user) return false;
    if (task.assigned_to_user_id === user.id) return true;
    if (task.assigned_to_group_id && myGroupIds.includes(task.assigned_to_group_id)) return true;
    return false;
  };

  // Claim is the one action the task's creator can also take (mirrors
  // TaskService.claimTask on the backend) — complete/reassign stay
  // assignee-only, so this must NOT be folded into canActOnTask above.
  const canClaimTask = (task: Task): boolean => {
    if (!user) return false;
    if (task.created_by === user.id) return true;
    return canActOnTask(task);
  };

  const isAssignedToMe = (task: Task): boolean => {
    if (!user) return false;
    if (task.assigned_to_user_id === user.id) return true;
    if (task.assigned_to_group_id && myGroupIds.includes(task.assigned_to_group_id)) return true;
    return false;
  };

  const isWatchingOnly = (task: Task): boolean => {
    if (!user) return false;
    if (isAssignedToMe(task)) return false;
    if (!task.watchers || task.watchers.length === 0) return false;
    return task.watchers.some((w) => {
      if (w.watcher_type === 'user' && w.user_id === user.id) return true;
      if (w.watcher_type === 'group' && w.group_id && myGroupIds.includes(w.group_id)) return true;
      return false;
    });
  };

  const getRowStyle = (task: Task): string => {
    if (isAssignedToMe(task)) return 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30';
    if (isWatchingOnly(task)) return 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30';
    return 'hover:bg-gray-50 dark:hover:bg-gray-700/50';
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const toggleWatcherUser = (id: string) => {
    setWatcherUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleWatcherGroup = (id: string) => {
    setWatcherGroupIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSort = (column: TaskSortColumn) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortArrow = (column: TaskSortColumn) => (
    <svg
      className={`w-3 h-3 inline-block ml-1 transition-transform ${sortBy === column && sortOrder === 'asc' ? 'rotate-180' : ''} ${sortBy === column ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const toggleSelectTask = (id: string) => {
    setSelectedTaskIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Bulk actions reuse the same single-task endpoints/permissions the
  // per-row action icons already use (claim/complete/reassign) — no new
  // backend endpoint. Task has no "closed" status (only todo/claimed/
  // completed, see models/Task.ts), so unlike the mockup there is no
  // bulk "Close" action; logged to docs/OPEN_ITEMS.md alongside the
  // similar priority-field mismatch.
  const isPrivilegedUser = user?.role === 'superadmin' || user?.role === 'manager';
  const canBulkReassign = (task: Task): boolean => isPrivilegedUser || (task.status === 'claimed' && canActOnTask(task));

  const runBulkAction = async (eligible: (task: Task) => boolean, call: (task: Task) => Promise<any>) => {
    const targets = tasks.filter((task) => selectedTaskIds.includes(task.id) && eligible(task));
    setBulkActionRunning(true);
    setBulkActionError(null);
    let failures = 0;
    for (const task of targets) {
      try {
        await call(task);
      } catch {
        failures++;
      }
    }
    if (failures > 0) {
      setBulkActionError(t('tasks.board.bulkPartialError', { failed: failures, total: targets.length }));
    }
    setSelectedTaskIds([]);
    setBulkAssigneeId('');
    setBulkActionRunning(false);
    await fetchAllData();
  };

  const handleBulkClaim = () => runBulkAction(
    (task) => task.status === 'todo' && canClaimTask(task),
    (task) => api.put(`/tasks/${task.id}/claim`)
  );

  const handleBulkComplete = () => {
    // Backend requires a completion note (min 2 chars) per complete call —
    // ask for ONE shared note for the whole bulk run (window.prompt, kept
    // simple like the incident bulk actions having no equivalent).
    const note = window.prompt(t('tasks.board.bulkCompleteNotePrompt'));
    if (note === null) return; // user cancelled
    if (!note.trim() || note.trim().length < 2) {
      setBulkActionError(t('tasks.board.bulkCompleteNoteRequired'));
      return;
    }
    runBulkAction(
      (task) => task.status === 'claimed' && canActOnTask(task),
      (task) => api.put(`/tasks/${task.id}/complete`, { note: note.trim() })
    );
  };

  const handleBulkAssign = () => {
    if (!bulkAssigneeId) return;
    runBulkAction(
      (task) => canBulkReassign(task),
      (task) => api.put(`/tasks/${task.id}/reassign`, { assigned_to_user_id: bulkAssigneeId })
    );
  };

  // Client-side filtering: apply all filters with AND logic, then sort.
  const filteredTasks = tasks.filter((task) => {
    if (filterTitle && !task.title.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterProjectId && task.project_id !== filterProjectId) return false;
    if (filterDomainIds.length > 0 && !filterDomainIds.includes(task.domain_id || '')) return false;
    if (filterStatus && task.status !== filterStatus) return false;
    if (filterPriority && task.priority !== filterPriority) return false;
    if (filterAssigneeId && task.assigned_to_user_id !== filterAssigneeId) return false;
    if (filterOpenOnly && task.status !== 'todo' && task.status !== 'claimed') return false;
    if (filterGroupMine && (!task.assigned_to_group_id || !myGroupIds.includes(task.assigned_to_group_id))) return false;
    if (filterCreatedByMe && task.created_by !== user?.id) return false;
    if (filterUnassigned && (task.assigned_to_user_id || task.assigned_to_group_id)) return false;
    return true;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let diff: number;
    switch (sortBy) {
      case 'title':
        diff = a.title.localeCompare(b.title);
        break;
      case 'status':
        diff = a.status.localeCompare(b.status);
        break;
      case 'task_number':
        diff = a.task_number - b.task_number;
        break;
      case 'created_at':
      default:
        diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortOrder === 'asc' ? diff : -diff;
  });

  const pagedTasks = sortedTasks.slice((page - 1) * pageSize, page * pageSize);

  const pagedTaskIds = pagedTasks.map((task) => task.id);
  const allOnPageSelected = pagedTaskIds.length > 0 && pagedTaskIds.every((id) => selectedTaskIds.includes(id));
  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedTaskIds((prev) => prev.filter((id) => !pagedTaskIds.includes(id)));
    } else {
      setSelectedTaskIds((prev) => Array.from(new Set([...prev, ...pagedTaskIds])));
    }
  };

  // Overview counts — reflect ALL fetched tasks, not the currently
  // filtered/paginated subset, so the row stays a stable "big picture"
  // summary while the table below is being filtered/sorted underneath it.
  const taskStats = {
    total: tasks.length,
    todo: tasks.filter((tsk) => tsk.status === 'todo').length,
    claimed: tasks.filter((tsk) => tsk.status === 'claimed').length,
    completed: tasks.filter((tsk) => tsk.status === 'completed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('tasks.taskManagement')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('tasks.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {canCreateTask && (
            <>
              <button
                onClick={openTemplatePicker}
                className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 w-full sm:w-auto justify-center"
              >
                {t('tasks.board.createFromTemplate')}
              </button>
              <button
                onClick={() => { resetForm(); setShowCreateTask(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('tasks.createNewTask')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.total}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.board.statsTotal')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{taskStats.todo}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.status.todo')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{taskStats.claimed}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.status.claimed')}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{taskStats.completed}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.status.completed')}</div>
        </div>
      </div>

      {/* Bulk action bar — Claim/Complete/Assign reuse the same
          endpoints and per-task permission rules as the per-row action
          icons; ineligible selected tasks are silently skipped. */}
      {selectedTaskIds.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {t('tasks.board.bulkSelectedCount', { count: selectedTaskIds.length })}
          </span>
          <button
            type="button"
            onClick={handleBulkClaim}
            disabled={bulkActionRunning}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
          >
            {t('tasks.board.bulkClaim')}
          </button>
          <button
            type="button"
            onClick={handleBulkComplete}
            disabled={bulkActionRunning}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
          >
            {t('tasks.board.bulkComplete')}
          </button>
          <div className="flex items-center gap-2">
            <select
              value={bulkAssigneeId}
              onChange={(e) => setBulkAssigneeId(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5"
            >
              <option value="">{t('tasks.board.bulkAssignPlaceholder')}</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleBulkAssign}
              disabled={bulkActionRunning || !bulkAssigneeId}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm disabled:opacity-50"
            >
              {t('tasks.board.bulkAssign')}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setSelectedTaskIds([])}
            className="text-sm text-blue-700 dark:text-blue-300 hover:underline ml-auto"
          >
            {t('tasks.board.bulkClearSelection')}
          </button>
        </div>
      )}
      {bulkActionError && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm">
          {bulkActionError}
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <Modal>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('tasks.board.templatePickerTitle')}</h2>
              <button
                onClick={() => setShowTemplatePicker(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-3">
              {templatesError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">{templatesError}</div>
              )}
              {templatesLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.detail.loading')}</p>
              )}
              {!templatesLoading && templates.length === 0 && !templatesError && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.board.noTemplates')}</p>
              )}
              {templates.map((tpl) => (
                <div key={tpl.id} className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{tpl.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleUseTemplate(tpl)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      {t('tasks.board.useTemplate')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteTemplate(tpl.id)}
                      disabled={templateDeletingId === tpl.id}
                      className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {/* Create Task Modal */}
      {showCreateTask && (
        <Modal>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('tasks.board.newTaskModalTitle')}</h2>
              <button
                onClick={() => setShowCreateTask(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateTask} className="p-6 space-y-5">
              {taskCreateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {taskCreateError}
                </div>
              )}

              {uploadWarnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm">
                  <ul className="list-disc list-inside">
                    {uploadWarnings.map((w, idx) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}


              {/* Task title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.title')} *</label>
                <input
                  type="text"
                  required
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('tasks.board.taskTitlePlaceholder')}
                />
              </div>

              {/* Task description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description')} *</label>
                <textarea
                  required
                  rows={3}
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('tasks.board.taskDescPlaceholder')}
                />
              </div>

              {/* Project */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Proje</label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.project_key})</option>
                  ))}
                </select>
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.board.domain')} *</label>
                <select
                  required
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('tasks.board.selectDomain')}</option>
                  {domains.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.priority')}</label>
                <select
                  value={taskPriority}
                  onChange={(e) => setTaskPriority(e.target.value as 'low' | 'medium' | 'high' | 'urgent')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">{t('tasks.detail.priority.low')}</option>
                  <option value="medium">{t('tasks.detail.priority.medium')}</option>
                  <option value="high">{t('tasks.detail.priority.high')}</option>
                  <option value="urgent">{t('tasks.detail.priority.urgent')}</option>
                </select>
              </div>

              {/* Assignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('common.assignedTo')}</label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center text-sm">
                    <input
                      type="radio"
                      name="assignType"
                      value="user"
                      checked={assignType === 'user'}
                      onChange={() => { setAssignType('user'); setAssignId(''); }}
                      className="mr-1"
                    />
                    {t('tasks.board.assignToUser')}
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="radio"
                      name="assignType"
                      value="group"
                      checked={assignType === 'group'}
                      onChange={() => { setAssignType('group'); setAssignId(''); }}
                      className="mr-1"
                    />
                    {t('tasks.board.assignToGroup')}
                  </label>
                </div>
                {assignType === 'user' ? (
                  <select
                    value={assignId}
                    onChange={(e) => setAssignId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('tasks.board.noAssignment')}</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={assignId}
                    onChange={(e) => setAssignId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('tasks.board.noAssignment')}</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Watcher Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('tasks.board.watcherUsers')}</label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                  {members.length === 0 && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">{t('tasks.board.noAssignment')}</p>
                  )}
                  {members.map((m) => (
                    <label key={m.id} className="flex items-center text-sm gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={watcherUserIds.includes(m.id)}
                        onChange={() => toggleWatcherUser(m.id)}
                        className="rounded"
                      />
                      {m.name} ({m.role})
                    </label>
                  ))}
                </div>
              </div>

              {/* Watcher Groups */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('tasks.board.watcherGroups')}</label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                  {groups.length === 0 && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">{t('tasks.board.noAssignment')}</p>
                  )}
                  {groups.map((g) => (
                    <label key={g.id} className="flex items-center text-sm gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={watcherGroupIds.includes(g.id)}
                        onChange={() => toggleWatcherGroup(g.id)}
                        className="rounded"
                      />
                      {g.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('tasks.board.images')}</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16V8m0 0l-3 3m3-3l3 3m-3-3v8m0 0l-3-3m3 3l3-3"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                      <label className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>{t('tasks.board.addImage')}</span>
                        <input
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleFileSelect}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">{t('tasks.board.dragDropOrClick')}</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('tasks.board.imageFormatHint')} ({t('tasks.board.maxTotalSize')})
                    </p>
                  </div>
                </div>

                {/* Preview list */}
                {selectedImages.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {selectedImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-2 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <img
                          src={img.preview}
                          alt={img.file.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{img.file.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(img.file.size)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelectedImage(idx)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title={t('common.remove')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowCreateTask(false)}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={taskCreating}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {taskCreating ? t('tasks.board.creating') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-3 text-gray-600 dark:text-gray-400">{t('tasks.detail.loading')}</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Task table */}
      {!loading && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('tasks.taskList')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('tasks.board.taskCount')}</p>
          </div>

          <div className="lg:hidden px-4 py-3 border-b border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-2">
            <input type="text" value={filterTitle} onChange={(e) => setFilterTitle(e.target.value)} placeholder={t('common.title')} className="col-span-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2" />
            <select value={filterProjectId} onChange={(e) => setFilterProjectId(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2">
              <option value="">{t('tasks.board.filterProject')}: {t('tasks.board.allOption')}</option>
              {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2">
              <option value="">{t('common.status')}: {t('tasks.board.allOption')}</option>
              <option value="todo">{t('tasks.detail.status.todo')}</option>
              <option value="claimed">{t('tasks.detail.status.claimed')}</option>
              <option value="completed">{t('tasks.detail.status.completed')}</option>
            </select>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2">
              <option value="">{t('common.priority')}: {t('tasks.board.allOption')}</option>
              <option value="low">{t('tasks.detail.priority.low')}</option>
              <option value="medium">{t('tasks.detail.priority.medium')}</option>
              <option value="high">{t('tasks.detail.priority.high')}</option>
              <option value="urgent">{t('tasks.detail.priority.urgent')}</option>
            </select>
            <select value={filterAssigneeId} onChange={(e) => setFilterAssigneeId(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2">
              <option value="">{t('common.assignedTo')}: {t('tasks.board.allOption')}</option>
              {members.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
            </select>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={t('tasks.board.bulkSelectAll')}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div>{t('tasks.board.filterProject')}</div>
                      <select
                        value={filterProjectId}
                        onChange={(e) => setFilterProjectId(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('tasks.board.allOption')}</option>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div className="group cursor-pointer select-none inline-flex items-center" onClick={() => handleSort('title')}>
                        {t('common.title')}{sortArrow('title')}
                      </div>
                      <input
                        type="text"
                        value={filterTitle}
                        onChange={(e) => setFilterTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={t('tasks.board.allOption')}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div>{t('tasks.board.domain')}</div>
                      <MultiSelectDropdown
                        options={domains.map((d) => ({ id: d.id, label: d.name }))}
                        selectedIds={filterDomainIds}
                        onChange={setFilterDomainIds}
                        allLabel={t('tasks.board.allOption')}
                        searchPlaceholder={t('common.search')}
                      />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div className="group cursor-pointer select-none inline-flex items-center" onClick={() => handleSort('status')}>
                        {t('common.status')}{sortArrow('status')}
                      </div>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('tasks.board.allOption')}</option>
                        <option value="todo">{t('tasks.detail.status.todo')}</option>
                        <option value="claimed">{t('tasks.detail.status.claimed')}</option>
                        <option value="completed">{t('tasks.detail.status.completed')}</option>
                      </select>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div className="group cursor-pointer select-none inline-flex items-center" onClick={() => handleSort('priority')}>
                        {t('common.priority')}{sortArrow('priority')}
                      </div>
                      <select
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('tasks.board.allOption')}</option>
                        <option value="low">{t('tasks.detail.priority.low')}</option>
                        <option value="medium">{t('tasks.detail.priority.medium')}</option>
                        <option value="high">{t('tasks.detail.priority.high')}</option>
                        <option value="urgent">{t('tasks.detail.priority.urgent')}</option>
                      </select>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div>{t('common.assignedTo')}</div>
                      <select
                        value={filterAssigneeId}
                        onChange={(e) => setFilterAssigneeId(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('tasks.board.allOption')}</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort('created_at')}
                        className="group flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {t('common.createdAt')}{sortArrow('created_at')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedTasks.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        {t('tasks.board.noTasks')}
                      </td>
                    </tr>
                  )}
                  {pagedTasks.map((task) => (
                    <tr key={task.id} className={`transition-colors ${getRowStyle(task)}`}>
                      <td className={`px-4 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.includes(task.id)}
                          onChange={() => toggleSelectTask(task.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('tasks.board.bulkSelectRow')}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm text-gray-500 dark:text-gray-400`}>
                        <Link to={`/tasks/${task.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-blue-600 dark:hover:text-blue-400">
                          {task.project_key}-{task.task_number}
                        </Link>
                      </td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} text-sm text-gray-900 dark:text-gray-100 max-w-xs`}>
                        <div className="flex items-stretch gap-2">
                          <span className={`w-1 flex-shrink-0 rounded-full ${getStatusBarColor(task.status)}`} aria-hidden="true" />
                          <Link to={`/tasks/${task.id}`} onClick={(e) => e.stopPropagation()} className="block min-w-0 flex-1 hover:text-blue-600 dark:hover:text-blue-400" title={task.title}>
                          <span className="flex items-center gap-1">
                            <span className="font-medium truncate">{task.title}</span>
                            {isWatchingOnly(task) && (
                              <span title={t('tasks.board.watchingTooltip')} className="flex-shrink-0 inline-flex">
                                <svg
                                  className="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </span>
                            )}
                          </span>
                          {task.description && density === 'comfortable' && (
                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{task.description}</span>
                          )}
                        </Link>
                        </div>
                      </td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm text-gray-500 dark:text-gray-400`}>
                        {getDomainName(task.domain_id)}
                      </td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>{getStatusBadge(task.status)}</td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap`}>{getPriorityBadge(task.priority)}</td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm text-gray-900 dark:text-gray-100`}>{getAssigneeName(task)}</td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm text-gray-500 dark:text-gray-400`}>{formatDate(task.created_at)}</td>
                      <td className={`px-6 ${density === 'compact' ? 'py-2' : 'py-4'} whitespace-nowrap text-sm`}>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => { setPanelFocusReassign(false); setSelectedTask(task); }}
                            disabled={!(task.status === 'todo' && canClaimTask(task))}
                            title={t('tasks.board.actionClaim')}
                            className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:hover:bg-transparent transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a1 1 0 011-1h11l-3 3 3 3H4" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPanelFocusReassign(false); setSelectedTask(task); }}
                            disabled={!(task.status === 'claimed' && canActOnTask(task))}
                            title={t('tasks.board.actionComplete')}
                            className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:hover:bg-transparent transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPanelFocusReassign(true); setSelectedTask(task); }}
                            disabled={!(task.status === 'claimed' && canActOnTask(task))}
                            title={t('tasks.board.actionReassign')}
                            className="p-1.5 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:hover:bg-transparent transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lg:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {sortedTasks.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('tasks.board.noTasks')}</p>
              )}
              {pagedTasks.map((task) => (
                <div key={task.id} className={`px-4 py-3 ${getRowStyle(task)}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selectedTaskIds.includes(task.id)} onChange={() => toggleSelectTask(task.id)} aria-label={t('tasks.board.bulkSelectRow')} className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <Link to={`/tasks/${task.id}`} className="text-xs text-gray-500 dark:text-gray-400">{task.project_key}-{task.task_number}</Link>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(task.created_at)}</span>
                      </div>
                      <Link to={`/tasks/${task.id}`} className="mt-1 block text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{task.title}</Link>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {getStatusBadge(task.status)}
                        {getPriorityBadge(task.priority)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">{getAssigneeName(task)}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <button type="button" onClick={() => { setPanelFocusReassign(false); setSelectedTask(task); }} disabled={!(task.status === 'todo' && canClaimTask(task))} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-200 dark:border-blue-900/40 text-blue-600 dark:text-blue-400 disabled:opacity-40">{t('tasks.board.actionClaim')}</button>
                        <button type="button" onClick={() => { setPanelFocusReassign(false); setSelectedTask(task); }} disabled={!(task.status === 'claimed' && canActOnTask(task))} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-green-200 dark:border-green-900/40 text-green-600 dark:text-green-400 disabled:opacity-40">{t('tasks.board.actionComplete')}</button>
                        <button type="button" onClick={() => { setPanelFocusReassign(true); setSelectedTask(task); }} disabled={!(task.status === 'claimed' && canActOnTask(task))} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-yellow-200 dark:border-yellow-900/40 text-yellow-600 dark:text-yellow-400 disabled:opacity-40">{t('tasks.board.actionReassign')}</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {sortedTasks.length > 0 && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={sortedTasks.length}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              />
            )}
        </div>
      )}

      {selectedTask && (
        <TaskDetailPanel
          task={selectedTask}
          statusBadge={getStatusBadge(selectedTask.status)}
          domainName={getDomainName(selectedTask.domain_id)}
          assigneeName={getAssigneeName(selectedTask)}
          createdAtLabel={formatDate(selectedTask.created_at)}
          canClaim={canClaimTask(selectedTask)}
          canAct={canActOnTask(selectedTask)}
          canReassign={isPrivilegedUser || canActOnTask(selectedTask)}
          members={members}
          groups={groups}
          initialShowReassign={panelFocusReassign}
          onClose={() => { setSelectedTask(null); setPanelFocusReassign(false); }}
          onActionSuccess={fetchAllData}
        />
      )}
    </div>
  );
};

export default Tasks;
